import { FormLabel, useToast } from '@chakra-ui/react';
import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { z } from 'zod';

import {
  CmBox,
  CmButton,
  CmFlex,
  CmFormControl,
  CmFormLabel,
  CmIconButton,
  CmInput,
  CmInputGroup,
  CmInputRightElement,
  CmText,
  ViewIcon,
  ViewOffIcon,
} from '@/components/uiParts';
import { routes } from '@/router';
import { changePassword, createStore } from '@/services/Kiosk';

import { LIST_QUERY_KEY } from '../index/useCamelKioskAccountsPage';

type Props = {
  storeId: string;
  kioskAccountId: string | null | undefined;
  storeName: string;
  type: 'create' | 'password-change';
};

const errorMessage =
  'パスワードは半角英数記号の中から8〜20文字（スペースは不可）';
const passwordSchema = z
  .string({ required_error: errorMessage })
  .min(8, { message: errorMessage })
  .max(20, { message: errorMessage })
  .regex(/^[!-~]*$/, { message: errorMessage });

const passwordFormSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: passwordSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'パスワードが一致しません',
    path: ['confirmPassword'],
  });

const AccountFrom: React.FC<Props> = ({
  type,
  storeId,
  storeName,
  kioskAccountId,
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const toast = useToast();
  const history = useHistory();
  const queryClient = useQueryClient();

  const { mutate: createAccount, isLoading: isCreatingAccount } = useMutation(
    createStore,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [LIST_QUERY_KEY] });
        toast({
          title: 'アカウントを作成しました',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        history.push(routes.CamelKioskAccounts.path);
      },
    }
  );

  const { mutate: updatePassword, isLoading: isPasswordUpdating } = useMutation(
    changePassword,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [LIST_QUERY_KEY] });
        toast({
          title: 'パスワードを変更しました',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        history.push(routes.CamelKioskAccounts.path);
      },
    }
  );

  const handleSubmit = async () => {
    const result = passwordFormSchema.safeParse({ password, confirmPassword });
    if (!result.success) {
      const errMessages = Array.from(
        new Set(result.error.errors.map((e) => e.message))
      );
      toast({
        title: errMessages.join('、'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    switch (type) {
      case 'create':
        await createAccount({
          masterStoreId: storeId,
          password: result.data.password,
        });
        break;
      case 'password-change':
        if (kioskAccountId) {
          await updatePassword({
            accountId: kioskAccountId,
            password: password,
          });
        }
        break;
    }
  };

  return (
    <div>
      <h1>
        {type === 'create'
          ? 'Kioskアカウント新規作成'
          : 'Kioskアカウントパスワード変更'}
      </h1>
      <CmBox mb={6}>
        <CmText fontSize="md" fontWeight="medium" mb={1}>
          店舗名
        </CmText>
        <CmText color="orange.400" fontSize="md">
          {storeName}
        </CmText>
      </CmBox>
      <CmBox mb={6}>
        <CmText fontSize="md" fontWeight="medium" mb={1}>
          ログインID
        </CmText>
        <CmText fontSize="md" color="gray.600">
          登録時に自動で発行されます
        </CmText>
      </CmBox>
      <CmFormControl mb={6}>
        <CmFlex align="center" mb={1}>
          <FormLabel fontSize="md" fontWeight="medium" m={0}>
            パスワード
          </FormLabel>
          <CmBox
            bg="red.50"
            px={2}
            py={0.5}
            borderRadius="md"
            fontSize="xs"
            color="red.500"
            fontWeight="medium"
            ml={2}
          >
            必須
          </CmBox>
        </CmFlex>
        <CmInputGroup>
          <CmInput
            type={showPassword ? 'text' : 'password'}
            placeholder="8文字以上の英数字"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            size="lg"
          />
          <CmInputRightElement h="full">
            <CmIconButton
              aria-label={
                showPassword ? 'パスワードを隠す' : 'パスワードを表示'
              }
              icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
              variant="ghost"
              onClick={() => setShowPassword(!showPassword)}
              size="sm"
            />
          </CmInputRightElement>
        </CmInputGroup>
      </CmFormControl>
      <CmFormControl mb={8}>
        <CmFlex align="center" mb={1}>
          <CmFormLabel fontSize="md" fontWeight="medium" m={0}>
            パスワード（確認用）
          </CmFormLabel>
          <CmBox
            bg="red.50"
            px={2}
            py={0.5}
            borderRadius="md"
            fontSize="xs"
            color="red.500"
            fontWeight="medium"
            ml={2}
          >
            必須
          </CmBox>
        </CmFlex>
        <CmInputGroup>
          <CmInput
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="パスワードを再入力"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            size="lg"
          />
          <CmInputRightElement h="full">
            <CmIconButton
              aria-label={
                showConfirmPassword ? 'パスワードを隠す' : 'パスワードを表示'
              }
              icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
              variant="ghost"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              size="sm"
            />
          </CmInputRightElement>
        </CmInputGroup>
      </CmFormControl>
      <CmButton
        width="100%"
        bg="gray.300"
        color="white"
        borderRadius="full"
        py={6}
        _hover={{ bg: 'gray.400' }}
        onClick={handleSubmit}
        isLoading={isCreatingAccount || isPasswordUpdating}
        isDisabled={!password || !confirmPassword}
      >
        設定する
      </CmButton>
    </div>
  );
};

export default AccountFrom;
